/**
 * PolicyStatusWidget
 * 
 * Shows whether policies are configured (any active policies exist).
 * Displays in Promise Health header and links to Policies settings page.
 * 
 * UX states:
 * - ✅ "Policies configured (3/5)" → click opens /dashboard/settings/policies
 * - ⚠️ "Policies not configured" → CTA "Configure policies" (same link)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPoliciesStatus } from '../api/policies';
import { Settings, CheckCircle, AlertTriangle } from 'lucide-react';

export default function PolicyStatusWidget({ brandId }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    
    async function load() {
      try {
        const data = await getPoliciesStatus(brandId);
        if (!cancelled) setStatus(data);
      } catch (err) {
        console.error('Failed to load policy status:', err);
        if (!cancelled) setStatus(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    
    if (brandId) {
      load();
    }
    
    return () => { cancelled = true; };
  }, [brandId]);

  const handleClick = () => {
    navigate('/dashboard/settings/policies');
  };

  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium bg-muted/50 border border-border animate-pulse">
        <div className="h-2 w-2 rounded-full bg-muted-foreground/30" />
        <span className="text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  const configured = status.configured;
  const activeCount = status.active_count || 0;
  const totalSupported = status.total_supported || 5;

  const label = configured
    ? `Policies configured (${activeCount}/${totalSupported})`
    : 'Policies not configured';

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold border transition-all duration-200
        ${configured 
          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-100 hover:bg-emerald-500/15 hover:border-emerald-500/40' 
          : 'bg-amber-400/10 border-amber-400/30 text-amber-100 hover:bg-amber-400/15 hover:border-amber-400/40'}`}
      title="Policies power Decision Pack and Slack approvals (optional but recommended)"
      data-testid="policy-status-widget"
    >
      {configured ? (
        <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
      ) : (
        <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
      )}
      <span>{label}</span>
      <Settings className="h-3 w-3 opacity-60" />
    </button>
  );
}
